/**
 * Inline half of the rich-text → LaTeX converter: reading styles off elements, turning
 * text and inline elements into styled segments, and writing segments out as LaTeX with
 * every special character escaped.
 *
 * The input is an HTML tree built by the site's own tokenizer (`html/tree.ts`), never the
 * browser's DOM and never inserted into the page, so a pasted `<script>` or `onerror`
 * attribute is just text that gets dropped.
 */

import type { ElementNode, HtmlNode } from "@/lib/tool-kit/developer/html/tree";
import { escapeLatex } from "@/lib/tool-kit/developer/latex-table";
import {
  decodeReference,
  isSafeUrl,
} from "@/lib/tool-kit/developer/markdown/text";

export type HeadingScheme = "section" | "chapter";
export type TableRules = "booktabs" | "hline" | "grid" | "none";
export type UnicodeMode = "keep" | "ligatures";

export interface RichTextOptions {
  /** `section`: h1 becomes \section. `chapter`: h1 becomes \chapter (book/report). */
  headingScheme: HeadingScheme;
  /** Numbered headings, or the starred \section* form. */
  numberedHeadings: boolean;
  /** Keep “curly” quotes, dashes and … as UTF-8, or write LaTeX's own ligatures. */
  unicode: UnicodeMode;
  /** Turn straight double quotes into opening and closing quotes. */
  fixQuotes: boolean;
  tableRules: TableRules;
  /** Wrap tables in a `table` float (with the caption, when the source had one). */
  tableFloat: boolean;
  /** Bold the header row of a table. */
  boldHeader: boolean;
  /** Treat the first row of every table as a header, even when it uses plain cells. */
  firstRowHeader: boolean;
  /** Wrap the body in \documentclass … \begin{document}. */
  fullDocument: boolean;
}

export const DEFAULT_RICH_TEXT_OPTIONS: RichTextOptions = {
  headingScheme: "section",
  numberedHeadings: true,
  unicode: "keep",
  fixQuotes: true,
  tableRules: "booktabs",
  tableFloat: true,
  boldHeader: true,
  firstRowHeader: false,
  fullDocument: false,
};

export interface RichTextStats {
  headings: number;
  paragraphs: number;
  listItems: number;
  tables: number;
  links: number;
  /** Links left as plain text: scripts, relative paths and in-page anchors. */
  linksDropped: number;
  codeBlocks: number;
  quotes: number;
  images: number;
  /** Cells with rowspan > 1: the text stays in its first row. */
  rowspans: number;
  /** Lists nested more than four deep, which LaTeX itself rejects. */
  deepLists: number;
}

export interface Style {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  code: boolean;
  sup: boolean;
  sub: boolean;
}

export type Seg =
  | { kind: "text"; text: string; style: Style }
  | { kind: "break" }
  | { kind: "link"; href: string; children: Seg[] }
  | { kind: "image"; alt: string };

export interface Ctx {
  opts: RichTextOptions;
  stats: RichTextStats;
  packages: Set<string>;
  nonLatin: Set<string>;
  /** Last character emitted, so a straight quote knows whether it opens or closes. */
  prev: string;
  inHeading: boolean;
  inLink: boolean;
  inCell: boolean;
  inItem: number;
  enumDepth: number;
  listDepth: number;
}

export const PLAIN: Style = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  code: false,
  sup: false,
  sub: false,
};

const RE_REFERENCE =
  /&(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});/g;
const RE_HTML_SPACE = /[ \t\n\r\f]+/g;
const RE_BOLD_WEIGHT = /^(?:bold|bolder|[6-9]00)$/;
const RE_LIGHT_WEIGHT = /^(?:normal|lighter|[1-5]00)$/;
const RE_MONO = /courier|consolas|monospace|menlo|monaco|lucida console/i;
const RE_OPENING_CONTEXT = /[\s([{—–“‘]/;
const RE_LEADING_SPACE = /^[ \t\n]+/;
const RE_TRAILING_SPACE = /[ \t\n]+$/;
const RE_URL_ENCODE = /[\\{}^|"<>` ]/g;
const RE_URL_LATEX = /[%#&]/g;
const RE_ABSOLUTE_URL = /^(?:https?:\/\/|mailto:)/i;
const RE_LINE_BREAKS = /\s*[\r\n]+\s*/g;
const RE_STYLE_DECL = /([a-z-]+)\s*:\s*([^;]+)/gi;

/** Decode `&amp;`, `&#233;` and friends; unknown references stay as written. */
export function decodeText(raw: string): string {
  return raw.replace(RE_REFERENCE, (ref) => decodeReference(ref) ?? ref);
}

export function attr(el: ElementNode, name: string): string | undefined {
  const found = el.attrs.find((a) => a.name === name);
  return found === undefined ? undefined : decodeText(found.value ?? "");
}

/** The declarations in an element's `style` attribute, lower-cased. */
export function styleMap(el: ElementNode): Map<string, string> {
  const map = new Map<string, string>();
  const raw = attr(el, "style");
  if (raw === undefined) {
    return map;
  }
  for (const match of raw.matchAll(RE_STYLE_DECL)) {
    map.set(match[1].toLowerCase(), match[2].trim().toLowerCase());
  }
  return map;
}

function decorationOf(styles: Map<string, string>): string {
  return `${styles.get("text-decoration") ?? ""} ${styles.get("text-decoration-line") ?? ""}`;
}

/** Fold an element's tag and inline styles into the style its text carries. */
export function styleFor(el: ElementNode, parent: Style, ctx: Ctx): Style {
  const style = { ...parent };
  switch (el.name) {
    case "b":
    case "strong":
      style.bold = true;
      break;
    case "i":
    case "em":
    case "cite":
    case "dfn":
    case "var":
      style.italic = true;
      break;
    case "u":
    case "ins":
      style.underline = true;
      break;
    case "s":
    case "strike":
    case "del":
      style.strike = true;
      break;
    case "code":
    case "kbd":
    case "samp":
    case "tt":
      style.code = true;
      break;
    case "sub":
      style.sub = true;
      break;
    case "sup":
      style.sup = true;
      break;
    default:
      break;
  }
  applyInlineStyles(el, style);
  if (ctx.inHeading) {
    style.bold = false;
  }
  if (ctx.inLink) {
    style.underline = false;
  }
  return style;
}

function applyInlineStyles(el: ElementNode, style: Style): void {
  const styles = styleMap(el);
  if (styles.size === 0) {
    return;
  }
  const weight = styles.get("font-weight");
  if (weight !== undefined) {
    if (RE_BOLD_WEIGHT.test(weight)) {
      style.bold = true;
    } else if (RE_LIGHT_WEIGHT.test(weight)) {
      style.bold = false;
    }
  }
  const slant = styles.get("font-style");
  if (slant === "italic" || slant === "oblique") {
    style.italic = true;
  } else if (slant === "normal") {
    style.italic = false;
  }
  const decoration = decorationOf(styles);
  if (decoration.includes("underline")) {
    style.underline = true;
  }
  if (decoration.includes("line-through")) {
    style.strike = true;
  }
  const align = styles.get("vertical-align");
  if (align === "super") {
    style.sup = true;
  } else if (align === "sub") {
    style.sub = true;
  }
  if (RE_MONO.test(styles.get("font-family") ?? "")) {
    style.code = true;
  }
}

/** Straight double quotes → curly, deciding open or close from the character before. */
function educateQuotes(text: string, ctx: Ctx): string {
  let out = "";
  for (const ch of text) {
    if (ch === '"') {
      const opening = ctx.prev === "" || RE_OPENING_CONTEXT.test(ctx.prev);
      out += opening ? "“" : "”";
    } else {
      out += ch;
    }
    ctx.prev = out.at(-1) ?? ctx.prev;
  }
  return out;
}

function pushText(
  out: Seg[],
  text: string,
  style: Style,
  ctx: Ctx,
  preformatted: boolean
): void {
  let value = decodeText(text);
  if (!preformatted) {
    value = value.replace(RE_HTML_SPACE, " ");
  }
  if (value === "") {
    return;
  }
  if (ctx.opts.fixQuotes && !style.code) {
    value = educateQuotes(value, ctx);
  } else {
    ctx.prev = value.at(-1) ?? ctx.prev;
  }
  out.push({ kind: "text", text: value, style });
}

/** Elements dropped together with everything inside them. */
export const DROPPED = new Set([
  "head",
  "style",
  "script",
  "title",
  "template",
  "svg",
  "math",
  "canvas",
  "iframe",
  "object",
  "embed",
  "video",
  "audio",
  "select",
  "textarea",
  "button",
  "input",
  "xml",
  "noscript",
  "meta",
  "link",
]);

/** Elements that start a new block rather than flowing inline. */
export const BLOCKS = new Set([
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "dl",
  "dt",
  "dd",
  "table",
  "blockquote",
  "pre",
  "hr",
  "section",
  "article",
  "aside",
  "header",
  "footer",
  "main",
  "nav",
  "figure",
  "figcaption",
  "address",
  "details",
  "summary",
  "form",
  "fieldset",
  "center",
  "body",
  "html",
]);

function linkSegs(el: ElementNode, style: Style, ctx: Ctx): Seg[] {
  const href = (attr(el, "href") ?? "").trim();
  const inner: Seg[] = [];
  const saved = ctx.inLink;
  ctx.inLink = true;
  collectInline(el.children, inner, style, ctx);
  ctx.inLink = saved;
  if (RE_ABSOLUTE_URL.test(href) && isSafeUrl(href)) {
    ctx.stats.links += 1;
    ctx.packages.add("hyperref");
    return [{ kind: "link", href, children: inner }];
  }
  if (href !== "") {
    ctx.stats.linksDropped += 1;
  }
  return inner;
}

/** Collect the inline content of `nodes` into `out`. Block elements are not entered. */
export function collectInline(
  nodes: HtmlNode[],
  out: Seg[],
  style: Style,
  ctx: Ctx,
  preformatted = false
): void {
  for (const node of nodes) {
    if (node.kind === "text") {
      pushText(out, node.raw, style, ctx, preformatted);
      continue;
    }
    if (node.kind !== "element" || DROPPED.has(node.name)) {
      continue;
    }
    if (node.name === "br") {
      out.push({ kind: "break" });
      ctx.prev = "";
    } else if (node.name === "img") {
      ctx.stats.images += 1;
      out.push({ kind: "image", alt: (attr(node, "alt") ?? "").trim() });
    } else if (node.name === "a") {
      out.push(...linkSegs(node, styleFor(node, style, ctx), ctx));
    } else {
      collectInline(node.children, out, styleFor(node, style, ctx), ctx);
    }
  }
}

const SPECIAL_SPACE = new Set([
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
]);
const INVISIBLE = new Set(["​", "‌", "‍", "⁠", "﻿"]);
const LIGATURES: Record<string, string> = {
  "‘": "`",
  "’": "'",
  "“": "``",
  "”": "''",
  "–": "--",
  "—": "---",
  "…": "\\ldots{}",
};

/** Characters pdfLaTeX's default UTF-8 setup handles without extra packages, roughly. */
function mayBreakPdfLatex(code: number): boolean {
  if (code <= 0x2_4f) {
    return false;
  }
  if (code >= 0x20_00 && code <= 0x20_6f) {
    return false;
  }
  return !(code === 0x20_ac || code === 0x21_22 || code === 0x21_16);
}

function mapChar(ch: string, ctx: Ctx): string {
  if (ch === " ") {
    return "~";
  }
  if (INVISIBLE.has(ch)) {
    return "";
  }
  if (SPECIAL_SPACE.has(ch)) {
    return " ";
  }
  const fixed = invisibleReplacement(ch);
  if (fixed !== undefined) {
    return fixed;
  }
  if (ctx.opts.unicode === "ligatures" && LIGATURES[ch] !== undefined) {
    return LIGATURES[ch];
  }
  const code = ch.codePointAt(0) ?? 0;
  if (mayBreakPdfLatex(code) && ctx.nonLatin.size < 12) {
    ctx.nonLatin.add(ch);
  }
  return escapeLatex(ch);
}

function invisibleReplacement(ch: string): string | undefined {
  switch (ch) {
    case "­":
      return "\\-";
    case " ":
    case " ":
      return "\\,";
    case "‑":
      return "\\mbox{-}";
    default:
      return undefined;
  }
}

export function escapeRich(text: string, ctx: Ctx): string {
  let out = "";
  for (const ch of text) {
    out += mapChar(ch, ctx);
  }
  return out;
}

function wrapStyle(core: string, style: Style): string {
  let out = core;
  if (style.code) {
    out = `\\texttt{${out}}`;
  }
  if (style.sub) {
    out = `\\textsubscript{${out}}`;
  }
  if (style.sup) {
    out = `\\textsuperscript{${out}}`;
  }
  if (style.bold) {
    out = `\\textbf{${out}}`;
  }
  if (style.italic) {
    out = `\\textit{${out}}`;
  }
  if (style.underline) {
    out = `\\underline{${out}}`;
  }
  return out;
}

function styleKey(style: Style): string {
  return [
    style.bold,
    style.italic,
    style.underline,
    style.strike,
    style.code,
    style.sup,
    style.sub,
  ].join();
}

/** Join neighbouring text segments that share a style so they wrap once. */
function mergeText(segs: Seg[]): Seg[] {
  const merged: Seg[] = [];
  for (const seg of segs) {
    const last = merged.at(-1);
    if (
      seg.kind === "text" &&
      last?.kind === "text" &&
      styleKey(last.style) === styleKey(seg.style)
    ) {
      last.text += seg.text;
    } else if (seg.kind === "text") {
      merged.push({ ...seg });
    } else {
      merged.push(seg);
    }
  }
  return merged;
}

function renderTextSeg(seg: Extract<Seg, { kind: "text" }>, ctx: Ctx): string {
  const lead = RE_LEADING_SPACE.exec(seg.text)?.[0] ?? "";
  const trail = RE_TRAILING_SPACE.exec(seg.text)?.[0] ?? "";
  const core = seg.text.slice(lead.length, seg.text.length - trail.length);
  const spaces = (value: string) => (value === "" ? "" : " ");
  if (core === "") {
    return spaces(seg.text);
  }
  const styled = wrapStyle(escapeRich(core, ctx), seg.style);
  if (seg.style.strike) {
    ctx.packages.add("ulem");
  }
  const body = seg.style.strike ? `\\sout{${styled}}` : styled;
  return `${spaces(lead)}${body}${spaces(trail)}`;
}

/** Percent-encode what \href cannot carry, then backslash-escape % # &. */
export function hrefText(url: string): string {
  const safe = url.replace(
    RE_URL_ENCODE,
    (ch) =>
      `%${(ch.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(2, "0")}`
  );
  return safe.replace(RE_URL_LATEX, (ch) => `\\${ch}`);
}

/** Write segments as one run of LaTeX. `breaks` says how <br> is written. */
export function renderSegs(
  segs: Seg[],
  ctx: Ctx,
  breakText = " \\\\\n"
): string {
  let out = "";
  for (const seg of mergeText(segs)) {
    if (seg.kind === "text") {
      out += renderTextSeg(seg, ctx);
    } else if (seg.kind === "break") {
      out += breakText;
    } else if (seg.kind === "link") {
      const inner = renderSegs(seg.children, ctx, " ").trim();
      const label =
        inner === "" ? `\\texttt{${escapeRich(seg.href, ctx)}}` : inner;
      out += `\\href{${hrefText(seg.href)}}{${label}}`;
    }
  }
  return out;
}

/** Collapse the line breaks and runs of spaces that headings and cells cannot hold. */
export function flatten(text: string): string {
  return text.replace(RE_LINE_BREAKS, " ").replace(RE_HTML_SPACE, " ").trim();
}

/** The image placeholders among `segs`, written as one LaTeX comment line each. */
export function imageComments(segs: Seg[]): string[] {
  const lines: string[] = [];
  for (const seg of segs) {
    if (seg.kind === "image") {
      const alt = flatten(seg.alt);
      lines.push(alt === "" ? "% image omitted" : `% image omitted: ${alt}`);
    }
  }
  return lines;
}
